import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { trackEvent } from '~/api/analytics';
import { Box, CustomSpinner, DataGrid, Pagination, Text } from '~/commonComponents';
import { DashboardJobsType, IDashboardJob } from '~/domain/DashboardJobs';
import { useUserSelector } from '~/hooks';
import { Colors } from '~/themes/colors';
import { FilterStatusEnum } from '~/utils/Enum';
import { useJobList } from '../../hooks';
import { DataGridCommonColumn } from '../DataGrid.column';
import { EmptyMessage } from '../EmptyMessage';

export const ScheduledTab = () => {
  const currentUser = useUserSelector();
  const navigate = useNavigate();

  const { data, isInitialLoading, isLoading, methods, pagination } = useJobList({
    jobType: DashboardJobsType.FILLED,
  });

  const handleRowClick = useCallback(
    (item: IDashboardJob) => {
      trackEvent('Shifts - Scheduled Shifts Tab - Shift Tile Clicked', {
        shiftId: item?.id,
        shiftSpecialty: item?.specialty,
        procedures: item?.procedures,
        breakTime: item?.lunchBreakTime,
        autoConfirm: item?.autoFill,
        softwares: currentUser?.office_info?.softwares,
        assistedHygiene: item?.isAssistedHygiene,
        tpp: item?.amount,
        professionalId: item?.professional?.id,
        dateTimeConfirmed: item?.jobFilledDatetime,
      });

      navigate(`/dashboard/job/${item.id}`);
    },
    [currentUser?.office_info?.softwares, navigate],
  );

  if (isInitialLoading) {
    return (
      <Box marginTop={32}>
        <CustomSpinner isLoading size={24} />
      </Box>
    );
  }

  return (
    <DataGrid
      EmptyComponent={<EmptyMessage type={FilterStatusEnum.SCHEDULED} />}
      PaginationComponent={
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={methods.setCurrentPage}
          maxButtons={5}
        />
      }
      columns={[
        DataGridCommonColumn.Office,
        {
          ...DataGridCommonColumn.Type,
          width: '160px',
        },
        {
          key: 'professional',
          header: 'Professional',
          width: '180px',
          render: (item) => (
            <Text bold>
              {item.professional
                ? [item.professional.firstName, item.professional.lastName].join(' ')
                : ''}
            </Text>
          ),
        },
        {
          ...DataGridCommonColumn.Details,
          width: '200px',
        },
        DataGridCommonColumn.Profession,
        DataGridCommonColumn.Rate,
        DataGridCommonColumn.Action,
      ]}
      data={data}
      isLoading={isLoading}
      getRowStyle={(item) => ({
        backgroundColor: item.dashboardJobApi.isOnsiteInterview
          ? Colors.jobs_interview_background
          : Colors.jobs_shift_background,
      })}
      keyExtractor={(item) => item.id}
      onRowClick={handleRowClick}
    />
  );
};
